import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Camera, CheckCircle2, Loader2,
  ImagePlus, Flag, AlertTriangle, FileText,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import WorkerLayout from '@/components/worker/WorkerLayout'
import Button from '@/components/ui/Button'

// ── Photo upload helper ───────────────────────────────────────────────────────

function PhotoUpload({ label, subtitle, type, bookingId, workerId, photos, onAdded }) {
  const inputRef   = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [err,       setErr]       = useState(null)

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setErr(null)
    const ext  = file.name.split('.').pop() || 'jpg'
    const path = `${workerId}/${bookingId}/${type}_${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage.from('job-photos').upload(path, file, { upsert: false })
    if (upErr) { setErr('Upload failed.'); setUploading(false); return }
    await supabase.from('booking_photos').insert({ booking_id: bookingId, uploaded_by: workerId, photo_type: type, storage_path: path })
    onAdded({ storage_path: path, photo_type: type })
    setUploading(false)
    e.target.value = ''
  }

  function getUrl(path) {
    const { data: { publicUrl } } = supabase.storage.from('job-photos').getPublicUrl(path)
    return publicUrl
  }

  return (
    <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
      <p className="text-sm font-semibold text-ink-primary mb-0.5">{label}</p>
      {subtitle && <p className="text-xs text-ink-muted mb-3">{subtitle}</p>}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {photos.map((p, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-surface-overlay">
              <img src={getUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
              <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-3 h-3 text-white" />
              </div>
            </div>
          ))}
        </div>
      )}
      {err && <p className="text-xs text-siglat-red mb-2">{err}</p>}
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-surface-border text-sm text-ink-secondary hover:border-siglat-teal hover:text-siglat-teal transition-colors w-full justify-center disabled:opacity-50"
      >
        {uploading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
          : <><ImagePlus className="w-4 h-4" /> {photos.length > 0 ? 'Add Another' : 'Take / Upload Photo'}</>
        }
      </button>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function CompleteJob() {
  const { id }   = useParams()
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [job,       setJob]       = useState(null)
  const [loading,   setLoading]   = useState(true)
  const [acting,    setActing]    = useState(false)
  const [err,       setErr]       = useState(null)
  const [done,      setDone]      = useState(false)
  const [available, setAvailable] = useState(false)
  const [notes,     setNotes]     = useState('')

  const [afterPhotos,    setAfterPhotos]    = useState([])
  const [materialPhotos, setMaterialPhotos] = useState([])

  useEffect(() => {
    if (!profile?.id) return
    fetchJob()
    fetchAvailability()
  }, [profile?.id, id]) // eslint-disable-line

  async function fetchAvailability() {
    const { data } = await supabase.from('worker_profiles').select('is_available').eq('id', profile.id).maybeSingle()
    if (data) setAvailable(data.is_available ?? false)
  }

  async function fetchJob() {
    setLoading(true)
    const { data, error } = await supabase
      .from('bookings')
      .select('id, ref_number, status, address, service_categories(name), booking_photos(photo_type, storage_path)')
      .eq('id', id)
      .eq('worker_id', profile.id)
      .single()

    if (error || !data) { setErr('Job not found.'); setLoading(false); return }

    // Already completed — show success
    if (data.status === 'completed' || data.status === 'pending_approval') {
      setDone(true)
      setLoading(false)
      return
    }

    setJob(data)
    const photos = data.booking_photos ?? []
    setAfterPhotos(photos.filter((p) => p.photo_type === 'after'))
    setMaterialPhotos(photos.filter((p) => p.photo_type === 'materials_receipt'))
    setLoading(false)
  }

  async function handleComplete() {
    if (afterPhotos.length === 0) { setErr('At least one after photo is required.'); return }
    setErr(null)
    setActing(true)

    const { error } = await supabase
      .from('bookings')
      .update({
        status:       'pending_approval',
        completed_at: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('worker_id', profile.id)

    if (error) { setErr('Failed to submit. Try again.'); setActing(false); return }
    supabase.functions.invoke('send-notification', { body: {
      type: 'admin_job_submitted', worker_id: profile.id,
      data: { ref_number: job.ref_number },
    }}).catch(console.error)
    setDone(true)
    setActing(false)
  }

  // ── Done screen ───────────────────────────────────────────────────────────────
  if (done) {
    return (
      <WorkerLayout available={available}>
        <div className="max-w-lg mx-auto px-4 py-16 flex flex-col items-center text-center gap-5">
          <div className="w-16 h-16 bg-green-500/10 border border-green-500/25 rounded-2xl flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-ink-primary mb-2">Job Submitted</h2>
            <p className="text-sm text-ink-muted leading-relaxed">
              Awaiting admin verification. Your payout will be released after approval.
            </p>
          </div>
          <Button onClick={() => navigate('/worker')} className="w-full">
            Back to Dashboard
          </Button>
        </div>
      </WorkerLayout>
    )
  }

  if (loading) {
    return (
      <WorkerLayout available={available}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
        </div>
      </WorkerLayout>
    )
  }

  if (!job) {
    return (
      <WorkerLayout available={available}>
        <div className="px-4 py-10 text-center">
          <AlertTriangle className="w-8 h-8 text-siglat-red mx-auto mb-3" />
          <p className="text-sm text-ink-secondary">{err ?? 'Job not found.'}</p>
          <button onClick={() => navigate('/worker')} className="mt-4 text-sm text-siglat-teal underline">
            Back to dashboard
          </button>
        </div>
      </WorkerLayout>
    )
  }

  return (
    <WorkerLayout available={available}>
      <div className="max-w-lg mx-auto px-4 py-5 flex flex-col gap-4">

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-primary transition-colors w-fit"
        >
          <ArrowLeft className="w-4 h-4" /> On Site
        </button>

        {/* Header */}
        <div>
          <h1 className="text-lg font-bold text-ink-primary">Complete Job</h1>
          <p className="text-xs text-ink-muted font-mono">{job.ref_number}</p>
          <p className="text-xs text-ink-muted mt-0.5">{job.service_categories?.name} · {job.address}</p>
        </div>

        {/* After photos */}
        <PhotoUpload
          label="After Photos"
          subtitle="Required — show completed work clearly"
          type="after"
          bookingId={id}
          workerId={profile.id}
          photos={afterPhotos}
          onAdded={(p) => setAfterPhotos((prev) => [...prev, p])}
        />

        {/* Materials receipt */}
        <PhotoUpload
          label="Materials Receipt"
          subtitle="Photo of receipt for materials purchased (if any)"
          type="materials_receipt"
          bookingId={id}
          workerId={profile.id}
          photos={materialPhotos}
          onAdded={(p) => setMaterialPhotos((prev) => [...prev, p])}
        />

        {/* Completion notes */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-ink-muted" />
            <p className="text-sm font-semibold text-ink-primary">Completion Notes</p>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Describe what was done, any issues encountered, parts replaced…"
            className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted resize-none focus:outline-none focus:ring-1 focus:ring-siglat-teal"
          />
        </div>

        {/* Error */}
        {err && (
          <div className="flex items-center gap-2 p-3 bg-siglat-red/10 border border-siglat-red/25 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-siglat-red shrink-0" />
            <p className="text-sm text-siglat-red">{err}</p>
          </div>
        )}

        {/* Submit */}
        <Button
          onClick={handleComplete}
          disabled={acting || afterPhotos.length === 0}
          className="w-full py-4 text-base"
        >
          {acting
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
            : <><Flag className="w-4 h-4" /> Submit for Admin Verification</>
          }
        </Button>

        <p className="text-xs text-ink-muted text-center leading-relaxed pb-4">
          Admin will verify before marking the job complete and releasing your payout.
        </p>

      </div>
    </WorkerLayout>
  )
}
